import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import { Masonry } from 'masonic';

import {
  SectionHeader,
  SectionHeadline,
  SectionTitle,
  SectionSuptitle,
  SectionBody,
  sectionCardGroupHeaderGrid,
  SectionCardGroup
} from '../styles/section';
import { CardBlog, CardProject } from '../components/cards';
import useMediaAware from '../utils/media-query-aware-hook';
import { prepareAuthorProperties } from '../utils/utils';
import { MasonryItemsListElement } from '../styles/masonry';

const latestItemsRenderFn = ({ index, data }) => {
  const { slug } = data;
  const { title, media } = data.frontmatter;
  const cardImage = getImage(media?.card?.url);

  if (data.internal.type === 'Blog') {
    const { excerpt, date, timeToRead } = data;
    const { topics, authors, teaser } = data.frontmatter;

    const authorsFields = prepareAuthorProperties(authors);

    return (
      <CardBlog
        title={title}
        slug={slug}
        readTime={timeToRead}
        cardImage={cardImage}
        authors={authorsFields}
        topics={topics}
        date={date}
        excerpt={teaser || excerpt}
        index={index + 1}
      />
    );
  }

  if (data.internal.type === 'Project') {
    const { client } = data.frontmatter;
    return (
      <CardProject
        title={title}
        slug={slug}
        cardImage={cardImage}
        client={client}
        index={index + 1}
        skin='dark'
      />
    );
  }

  return null;
};

/**
 * Builds the ordered list of items rendered by <SectionLatestWork /> from the
 * `latestBlog` / `latestProject` results of a page query.
 *
 * The data is fetched through the page query of each page that renders the
 * section (the blog post template and the homepage) rather than via
 * `useStaticQuery`. Static queries over content collections (allBlog/allProject)
 * are fragile in `gatsby develop`: saving an MDX file can briefly deliver an
 * empty result for the query, which makes `useStaticQuery` throw and crashes the
 * dev server. Page-query data is delivered through Gatsby's loader, which does
 * not throw on a transient miss.
 *
 * Keeping the selection/ordering logic in this hook means it is defined once and
 * shared by every page that renders the section.
 *
 * @param {object} params
 * @param {object} [params.latestBlog] `latestBlog` connection from the page query.
 * @param {object} [params.latestProject] `latestProject` connection from the page query.
 * @param {string} [params.excludeId] Node id to omit (e.g. the post being viewed).
 * @returns {Array} Ordered blog + project nodes to render.
 */
export function useLatestWork({ latestBlog, latestProject, excludeId } = {}) {
  return useMemo(() => {
    // We're getting up to 4 blog posts to ensure we always have at least 3
    // after excluding the current one.
    const blogNodes = (latestBlog?.nodes ?? [])
      .filter((node) => node.id !== excludeId)
      .slice(0, 3);
    const projectNodes = latestProject?.nodes ?? [];

    return [...blogNodes, ...projectNodes];
  }, [latestBlog, latestProject, excludeId]);
}

function SectionLatestWork({ items = [] }) {
  const [ref, media] = useMediaAware();

  // In this case our reference is the body, and not a specific element.
  useEffect(() => {
    ref(document.body);
  }, [ref]);

  return (
    <SectionCardGroup>
      <SectionHeader grid={sectionCardGroupHeaderGrid}>
        <SectionHeadline>
          <SectionTitle>What we&apos;re doing.</SectionTitle>
          <SectionSuptitle>Latest</SectionSuptitle>
        </SectionHeadline>
      </SectionHeader>
      <SectionBody>
        <Masonry
          items={items}
          overscanBy={Infinity}
          columnGutter={
            media
              ? media.isXsmallDown()
                ? 16
                : media.isLargeDown()
                  ? 32
                  : 48
              : null
          }
          columnCount={media ? (media.isSmallDown() ? 1 : 2) : null}
          columnWidth={288}
          as={MasonryItemsListElement}
          itemAs='li'
          itemKey={(item) => item.id}
          render={latestItemsRenderFn}
        />
      </SectionBody>
    </SectionCardGroup>
  );
}

SectionLatestWork.propTypes = {
  // The ordered list of blog/project nodes to display, as produced by
  // `useLatestWork`.
  items: PropTypes.array
};

export default SectionLatestWork;

// Data requirements for <SectionLatestWork />, colocated with the component.
//
// Defined as a fragment on the root `Query` type so every page that renders the
// section (the blog post template and the homepage) can pull the same
// blog/project selection into its page query with `...LatestWorkData`, instead
// of duplicating the block. Gatsby `graphql` tags must be static, so a shared
// fragment is the only way to DRY this across page queries.
export const latestWorkFragment = graphql`
  fragment LatestWorkData on Query {
    latestProject: allProject(
      filter: { published: { eq: true } }
      limit: 1
      sort: { date: DESC }
    ) {
      nodes {
        internal {
          type
        }
        ...ProjectCardData
      }
    }

    latestBlog: allBlog(
      filter: { published: { eq: true } }
      limit: 4
      sort: { date: DESC }
    ) {
      nodes {
        id
        internal {
          type
        }
        ...BlogCardData
      }
    }
  }
`;
